import React, { useCallback, useEffect, useRef, useState } from "react";
import { splitSelection, togglePieSelection } from "../utils/pieSelection";
import type { ComponentCombinationItem, ComponentCombinations } from "../types/api";
import type { ComponentCombinationsParams } from "../api/client";
import { fetchComponentCombinations } from "../api";
import { ComboDescription } from "./ComboDescription";
import { HelpHint } from "./HelpHint";
import {
    COLOUR_SLICE_GROUPING,
    DistributionChart,
    PRODUCT_TYPE_SLICE_GROUPING,
} from "./DistributionChart";
import { Pagination } from "./Pagination";
import {
    COMBO_QUANTITY_COL,
    genericItemColTooltip,
    genericListTooltip,
    genericPctColTooltip,
    productTypeChartTooltip,
    productTypeCombinationColTooltip,
    productTypeListTooltip,
    productTypePctColTooltip,
    withQtyItemColTooltip,
    withQtyListTooltip,
    withQtyPctColTooltip,
} from "../utils/combinationListTooltips";
import { colourMatchKey, normalizeColourSpelling } from "../utils/colourLabels";
import {
    CONTEXT_MENU_BLOCKERS,
    isMultiSelectClick,
    mergeLongPressHandlers,
    longPressPulseOpacity,
    useLongPressMultiSelect,
    useLongPressPulse,
} from "../utils/multiSelectGesture";
import gestureStyles from "../utils/multiSelectGesture.module.css";
import styles from "./ComponentSection.module.css";

const DEFAULT_PAGE_SIZE = 10;

interface ComponentSectionProps {
    /** Short label used in headings, charts, and text (e.g. "Fixed Interior") */
    title: string;
    data: ComponentCombinations | null;
    loading?: boolean;
    error?: Error | null;
    /** Items per page (default 10) */
    pageSize?: number;
    /** Currently selected color filter */
    selectedColor?: string;
    /** Called when a color is clicked in the pie chart */
    onColorClick?: (color: string) => void;
    /** Currently selected family filter */
    selectedFamily?: string;
    /** Called when a family is clicked in the pie chart */
    onFamilyClick?: (family: string) => void;
    /** Optional filter shown first in the left chart column */
    leadingFilter?: React.ReactNode;
    /** Base params for fetching pages (enables server-side pagination) */
    fetchParams?: ComponentCombinationsParams;
    /** Called when a generic row is clicked (for cross-tab persistence) */
    onGenericClick?: (description: string) => void;
    /** Currently selected generic item (from parent, survives tab switches) */
    selectedGenericItem?: string;
    /** Currently selected product type filter */
    selectedProductType?: string;
    /** Called when a product type row is clicked */
    onProductTypeClick?: (value: string) => void;
    /** Optional drawer-runner type split (push-open vs soft-closing); drawer_fronts tab only */
    runnerTypeDistribution?: ComponentCombinationItem[];
    /** Currently selected runner type filter (comma-separated labels, multi-select) */
    selectedRunnerType?: string;
    /** Called when a runner type segment is clicked in the pie chart */
    onRunnerTypeClick?: (value: string) => void;
    /** Show a product-type pie chart above the product-type table (interior / loose-interior tabs) */
    showProductTypeChart?: boolean;
    /** Called when the user clicks retry after a failed load */
    onRetry?: () => void;
    /** Selected size filter for the Top Panels tab */
    selectedTopPanelSize?: string;
    /** Called when a top-panel size segment is clicked */
    onTopPanelSizeClick?: (size: string) => void;
}

/**
 * Displays a component section (interior, lighting, or knobs) with:
 * - Left column: color & family pie charts, product-type list, and attach-rate text
 * - Middle column: family dropdown + generic combinations (ignoring item count)
 * - Right column: family dropdown + exact item-count combinations
 * All lists have total footers and pagination.
 */
export function ComponentSection({
    title,
    data,
    loading,
    error,
    pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
    selectedColor,
    onColorClick,
    selectedFamily: externalFamily,
    onFamilyClick,
    leadingFilter,
    fetchParams,
    onGenericClick,
    selectedGenericItem,
    selectedProductType,
    onProductTypeClick,
    runnerTypeDistribution,
    selectedRunnerType,
    onRunnerTypeClick,
    showProductTypeChart,
    onRetry,
    selectedTopPanelSize,
    onTopPanelSizeClick,
}: ComponentSectionProps) {
    const [genericPage, setGenericPage] = useState(1);
    const [genericPageSize, setGenericPageSize] = useState(initialPageSize);
    const [withQtyPage, setWithQtyPage] = useState(1);
    const [withQtyPageSize, setWithQtyPageSize] = useState(initialPageSize);
    const [ptPage, setPtPage] = useState(1);
    const [ptPageSize, setPtPageSize] = useState(initialPageSize);
    const [selectedGeneric, setSelectedGeneric] = useState("");
    // Local data for page changes (falls back to parent-provided data)
    const [localData, setLocalData] = useState<ComponentCombinations | null>(null);
    const [genericFetchCount, setGenericFetchCount] = useState(0);
    const [withQtyFetchCount, setWithQtyFetchCount] = useState(0);
    const currentData = localData || data;
    const productTypeTitle = `${title} product types`;

    // Reset when parent data changes (filter change, initial load)
    useEffect(() => {
        setLocalData(null);
        // Restore selection from parent if available
        if (selectedGenericItem && data?.generic.some((item) => item.description === selectedGenericItem)) {
            setSelectedGeneric(selectedGenericItem);
        } else if (!selectedGenericItem) {
            setSelectedGeneric("");
            setGenericPage(1);
            setWithQtyPage(1);
            setPtPage(1);
        }
    }, [data]);

    // Sync selection from parent (e.g. pill removed, tab switch restore)
    useEffect(() => {
        if (!selectedGenericItem && selectedGeneric) {
            // Parent cleared selection (pill removed) - clear local state
            setSelectedGeneric("");
            setWithQtyPage(1);
            setLocalData(null);
        } else if (selectedGenericItem && selectedGenericItem !== selectedGeneric) {
            // Parent set a new selection - update local state
            setSelectedGeneric(selectedGenericItem);
            setWithQtyPage(1);
            fetchPage({ genericFilter: selectedGenericItem, withQtyPage: 1 });
        }
    }, [selectedGenericItem]);

    // Fetch a specific page from the backend
    const fetchPage = useCallback(
        async (opts: {
            genericPage?: number;
            withQtyPage?: number;
            genericFilter?: string | null; // null = clear filter, undefined = use current
            genericSize?: number;
            withQtySize?: number;
        }) => {
            if (!fetchParams) return;
            const changesGeneric = opts.genericPage !== undefined || opts.genericSize !== undefined;
            const filterChanged = opts.genericFilter !== undefined; // null or string both count
            const changesWithQty = opts.withQtyPage !== undefined || opts.withQtySize !== undefined || filterChanged;
            if (changesGeneric || (!changesGeneric && !changesWithQty)) setGenericFetchCount((c) => c + 1);
            if (changesWithQty || (!changesGeneric && !changesWithQty)) setWithQtyFetchCount((c) => c + 1);
            try {
                const filter = filterChanged ? opts.genericFilter || undefined : selectedGeneric || undefined;
                const result = await fetchComponentCombinations({
                    ...fetchParams,
                    generic_page: opts.genericPage ?? genericPage,
                    generic_page_size: opts.genericSize ?? genericPageSize,
                    with_qty_page: opts.withQtyPage ?? withQtyPage,
                    with_qty_page_size: opts.withQtySize ?? withQtyPageSize,
                    generic_filter: filter,
                });
                setLocalData(result);
            } catch {
                // Ignore — keep showing current data
            } finally {
                if (changesGeneric || (!changesGeneric && !changesWithQty)) setGenericFetchCount((c) => c - 1);
                if (changesWithQty || (!changesGeneric && !changesWithQty)) setWithQtyFetchCount((c) => c - 1);
            }
        },
        [fetchParams, genericPage, withQtyPage, selectedGeneric, genericPageSize, withQtyPageSize]
    );

    // Family is managed externally via URL params
    const selectedFamily = externalFamily ?? "";

    // Own-dimension filter → cached baseline (highlight-only). Other dimension → live API (cross-filter).
    const [cachedColorDist, setCachedColorDist] = useState<ComponentCombinationItem[]>([]);
    useEffect(() => {
        if (
            currentData?.color_distribution &&
            currentData.color_distribution.length > 0 &&
            !selectedColor &&
            !selectedFamily
        ) {
            setCachedColorDist(currentData.color_distribution);
        }
    }, [currentData?.color_distribution, selectedColor, selectedFamily]);
    // The backend keeps every colour bucket while a colour filter is active
    // (highlight-only) and still applies all other filters, so prefer live
    // data. The cache only covers the gap before the first response.
    const liveColorDist = currentData?.color_distribution ?? [];
    const stableColorDist = liveColorDist.length > 0 ? liveColorDist : cachedColorDist;

    const [cachedFamilyDist, setCachedFamilyDist] = useState<ComponentCombinationItem[]>([]);
    useEffect(() => {
        if (
            currentData?.family_distribution &&
            currentData.family_distribution.length > 0 &&
            !selectedFamily &&
            !selectedColor
        ) {
            setCachedFamilyDist(currentData.family_distribution);
        }
    }, [currentData?.family_distribution, selectedFamily, selectedColor]);
    const liveFamilyDist = currentData?.family_distribution ?? [];
    const stableFamilyDist = liveFamilyDist.length > 0 ? liveFamilyDist : cachedFamilyDist;

    // Cache product type distribution when no product type filter is active
    const [cachedProductTypeDist, setCachedProductTypeDist] = useState<ComponentCombinationItem[]>([]);
    useEffect(() => {
        if (
            currentData?.product_type_distribution &&
            currentData.product_type_distribution.length > 0 &&
            !selectedProductType
        ) {
            setCachedProductTypeDist(currentData.product_type_distribution);
        }
    }, [currentData?.product_type_distribution, selectedProductType]);
    // Use cache when filter is active OR when we have a cache but current data
    // is stale (e.g., briefly after deselecting, before unfiltered response arrives)
    const liveProductTypeDist = currentData?.product_type_distribution ?? [];
    const stableProductTypeDist =
        liveProductTypeDist.length > 0 ? liveProductTypeDist : cachedProductTypeDist;

    // Auto-navigate product type list to page containing selected item
    useEffect(() => {
        if (selectedProductType && stableProductTypeDist.length > 0) {
            const selected = new Set(selectedProductType.split("|").filter(Boolean));
            const idx = stableProductTypeDist.findIndex((pt) => selected.has(pt.description));
            if (idx >= 0) setPtPage(Math.floor(idx / ptPageSize) + 1);
        }
    }, [stableProductTypeDist, selectedProductType, ptPageSize]);

    const pendingProductTypeRef = useRef<string | null>(null);
    const { pulseKey: productTypePulseKey, triggerPulse: triggerProductTypePulse } = useLongPressPulse();
    const applyProductTypeFilter = useCallback(
        (description: string, multiSelect: boolean) => {
            if (!onProductTypeClick) return;
            onProductTypeClick(
                togglePieSelection(
                    splitSelection(selectedProductType, "|"),
                    description,
                    stableProductTypeDist.map((pt) => pt.description),
                    multiSelect
                ).join("|")
            );
        },
        [onProductTypeClick, selectedProductType, stableProductTypeDist]
    );
    const {
        pressHandlers: productTypePressHandlers,
        consumeLongPress: consumeProductTypeLongPress,
        consumeSuppressClick: consumeProductTypeSuppressClick,
    } = useLongPressMultiSelect({
        onLongPressRecognized: () => {
            if (pendingProductTypeRef.current) triggerProductTypePulse(pendingProductTypeRef.current);
        },
        onLongPress: () => {
            const description = pendingProductTypeRef.current;
            if (description) {
                return () => applyProductTypeFilter(description, true);
            }
            return false;
        },
    });
    const productTypeRowPressHandlers = mergeLongPressHandlers(
        productTypePressHandlers,
        (e) => {
            const row = (e.target as HTMLElement).closest("tr[data-product-type]");
            pendingProductTypeRef.current = row?.getAttribute("data-product-type") ?? null;
        },
        () => {
            pendingProductTypeRef.current = null;
        }
    );

    // Pie clicks: regular click exclusive; ⌘-click or long-press multi-select
    // (from no selection: all except the clicked slice).
    const handleFamilyClick = (family: string, multiSelect: boolean, _event?: React.MouseEvent) => {
        onFamilyClick?.(
            togglePieSelection(
                splitSelection(selectedFamily),
                family,
                stableFamilyDist.map((f) => f.description),
                multiSelect
            ).join(",")
        );
        setGenericPage(1);
        setWithQtyPage(1);
    };

    const handleRunnerClick = (label: string, multiSelect: boolean, _event?: React.MouseEvent) => {
        onRunnerTypeClick?.(
            togglePieSelection(
                splitSelection(selectedRunnerType),
                label,
                (runnerTypeDistribution ?? []).map((r) => r.description),
                multiSelect
            ).join(",")
        );
    };

    if (loading && !currentData) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>Loading...</div>
            </div>
        );
    }

    if (error && !currentData) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    Failed to load
                    {onRetry && (
                        <button type="button" className={styles.retryBtn} onClick={onRetry} title="Retry">
                            ↻
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (
        !currentData ||
        (currentData.total_combinations === 0 && (currentData.product_type_distribution?.length ?? 0) === 0)
    ) {
        return (
            <div className={styles.container}>
                <div className={styles.empty}>No {title.toLowerCase()} combinations found</div>
            </div>
        );
    }

    const isStale = loading && !!currentData;
    const isGenericStale = genericFetchCount > 0;
    const isWithQtyStale = withQtyFetchCount > 0;

    return (
        <div className={styles.container}>
            <div className={`${styles.threeColumns} ${isStale ? styles.stale : ""}`}>
                {/* Left column: pie chart(s) */}
                <div className={styles.leftColumn}>
                    {leadingFilter}
                    {(() => {
                        const colorChart = stableColorDist.length > 0 && (
                            <DistributionChart
                                key="color-dist"
                                title={`${title} colours`}
                                data={stableColorDist.map((c) => ({
                                    label: c.description,
                                    value: c.count,
                                }))}
                                selectedValue={selectedColor}
                                valueSeparator="|"
                                formatLabel={normalizeColourSpelling}
                                matchLabel={colourMatchKey}
                                grouping={COLOUR_SLICE_GROUPING}
                                onSegmentClick={
                                    onColorClick
                                        ? (color, multiSelect) =>
                                              onColorClick(
                                                  togglePieSelection(
                                                      splitSelection(selectedColor, "|"),
                                                      color,
                                                      stableColorDist.map((c) => c.description),
                                                      multiSelect
                                                  ).join("|")
                                              )
                                        : undefined
                                }
                            />
                        );
                        const familyChart = stableFamilyDist.length > 1 && (
                            <DistributionChart
                                key="family-dist"
                                title={`${title} families`}
                                data={stableFamilyDist.map((f) => ({
                                    label: f.description,
                                    value: f.count,
                                }))}
                                selectedValue={selectedFamily}
                                onSegmentClick={handleFamilyClick}
                            />
                        );
                        const topPanelSizeChart =
                            currentData.component_type === "top_panels" &&
                            (currentData.top_panel_size_distribution?.length ?? 0) > 0 ? (
                                <DistributionChart
                                    key="top-panel-size-dist"
                                    title="Top panel sizes"
                                    data={(currentData.top_panel_size_distribution ?? []).map((s) => ({
                                        label: s.description,
                                        value: s.count,
                                    }))}
                                    selectedValue={selectedTopPanelSize}
                                    valueSeparator="|"
                                    onSegmentClick={(size, multiSelect) =>
                                        onTopPanelSizeClick?.(
                                            togglePieSelection(
                                                splitSelection(selectedTopPanelSize, "|"),
                                                size,
                                                (currentData.top_panel_size_distribution ?? []).map(
                                                    (d) => d.description
                                                ),
                                                multiSelect
                                            ).join("|")
                                        )
                                    }
                                />
                            ) : null;
                        const productTypeChart = showProductTypeChart && stableProductTypeDist.length > 1 && (
                            <DistributionChart
                                key="product-type-dist"
                                title={productTypeTitle}
                                helpText={productTypeChartTooltip(title)}
                                data={stableProductTypeDist.map((pt) => ({
                                    label: pt.description,
                                    value: pt.count,
                                }))}
                                selectedValue={selectedProductType}
                                valueSeparator="|"
                                wrapLegendLabels
                                grouping={PRODUCT_TYPE_SLICE_GROUPING}
                                onSegmentClick={
                                    onProductTypeClick
                                        ? (value, multiSelect) => applyProductTypeFilter(value, multiSelect)
                                        : undefined
                                }
                            />
                        );
                        return (
                            <>
                                {familyChart}
                                {colorChart}
                                {productTypeChart}
                                {topPanelSizeChart}
                            </>
                        );
                    })()}
                    {/* Drawer runner type split (push-open vs soft-closing); fronts tab only.
                        Runners are independent of front type (doors-only combos can carry
                        drawer runners), so a single runner type is still worth showing.
                        Hidden only when nothing has runners (e.g. PAX) and no runner filter
                        is active; with a filter active it stays so the user can clear it. */}
                    {runnerTypeDistribution &&
                        (Boolean(selectedRunnerType) ||
                            runnerTypeDistribution.some((r) => r.description !== "No runner" && r.count > 0)) && (
                            <DistributionChart
                                key="runner-dist"
                                title="Drawer runners"
                                helpText="Runner type of the drawer frames in the combination. 'No runner' means the combination has fronts but no drawer frame with runners."
                                data={runnerTypeDistribution.map((r) => ({
                                    label: r.description,
                                    value: r.count,
                                }))}
                                selectedValue={selectedRunnerType || undefined}
                                onSegmentClick={onRunnerTypeClick ? handleRunnerClick : undefined}
                            />
                        )}
                    {stableProductTypeDist.length > 1 && (
                        <div className={styles.productTypeList} {...CONTEXT_MENU_BLOCKERS}>
                            <h4 className={styles.columnTitle}>
                                {productTypeTitle} <HelpHint text={productTypeListTooltip(title)} />
                            </h4>
                            {(() => {
                                const entries = stableProductTypeDist;
                                const totalEntries = entries.length;
                                const totalQty = entries.reduce((sum, pt) => sum + pt.count, 0);
                                const startIdx = (ptPage - 1) * ptPageSize;
                                const pageEntries = entries.slice(startIdx, startIdx + ptPageSize);
                                return (
                                    <>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th className={styles.descCol}>
                                                        <HelpHint text={productTypeCombinationColTooltip(title)}>
                                                            Combination
                                                        </HelpHint>
                                                    </th>
                                                    <th className={styles.numCol}>
                                                        <HelpHint text={COMBO_QUANTITY_COL}>Receipts</HelpHint>
                                                    </th>
                                                    <th className={styles.numCol}>
                                                        <HelpHint text={productTypePctColTooltip()}>%</HelpHint>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageEntries.map((pt, i) => {
                                                    const isNoFixed = pt.description === "No fixed interior";
                                                    const selectedSet = new Set(
                                                        (selectedProductType || "").split("|").filter(Boolean)
                                                    );
                                                    const isSelected = selectedSet.has(pt.description);
                                                    const hasSelection = selectedSet.size > 0;
                                                    const isDimmed = hasSelection && !isSelected;
                                                    const pct = totalQty > 0 ? (100 * pt.count) / totalQty : 0;
                                                    return (
                                                        <tr
                                                            key={i}
                                                            {...productTypeRowPressHandlers}
                                                            data-product-type={pt.description}
                                                            className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""} ${isDimmed && productTypePulseKey !== pt.description ? styles.dimmedRow : ""} ${isNoFixed ? styles.noFixedRow : ""} ${productTypePulseKey === pt.description ? gestureStyles.longPressPulse : ""}`}
                                                            style={
                                                                productTypePulseKey === pt.description
                                                                    ? {
                                                                          opacity: longPressPulseOpacity(
                                                                              isSelected ? 1 : 0.45,
                                                                              true
                                                                          ),
                                                                      }
                                                                    : undefined
                                                            }
                                                            onClick={(e) => {
                                                                if (!onProductTypeClick) return;
                                                                if (consumeProductTypeSuppressClick()) return;
                                                                applyProductTypeFilter(
                                                                    pt.description,
                                                                    isMultiSelectClick(e, consumeProductTypeLongPress())
                                                                );
                                                            }}
                                                        >
                                                            <td className={styles.descCol}>
                                                                <ComboDescription text={pt.description} />
                                                            </td>
                                                            <td className={styles.numCol}>
                                                                {pt.count.toLocaleString()}
                                                            </td>
                                                            <td className={styles.numCol}>{pct.toFixed(1)}%</td>
                                                        </tr>
                                                    );
                                                })}
                                                <tr className={styles.totalRow}>
                                                    <td className={styles.descCol}>
                                                        <strong>Total</strong>
                                                    </td>
                                                    <td className={styles.numCol}>
                                                        <strong>{totalQty.toLocaleString()}</strong>
                                                    </td>
                                                    <td className={styles.numCol}>
                                                        <strong>100.0%</strong>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <Pagination
                                            currentPage={ptPage}
                                            totalItems={totalEntries}
                                            pageSize={ptPageSize}
                                            onPageChange={setPtPage}
                                            onPageSizeChange={(s) => {
                                                setPtPageSize(s);
                                                setPtPage(1);
                                            }}
                                        />
                                    </>
                                );
                            })()}
                        </div>
                    )}
                    {/* Attach rate text */}
                    <div className={styles.attachRate}>
                        {currentData.attach_rate.percentage.toFixed(1)}% of combinations include {title.toLowerCase()}
                        <span className={styles.attachCount}>
                            ({currentData.total_combinations.toLocaleString()} combos)
                        </span>
                    </div>
                </div>

                {/* Middle column: generic list (ignoring item count) */}
                <div className={`${styles.middleColumn} ${isGenericStale ? styles.stale : ""}`}>
                    <h4 className={styles.columnTitle}>
                        {title} Combinations
                        <HelpHint text={genericListTooltip(title)} className={styles.subtitle}>
                            (any item count)
                        </HelpHint>
                    </h4>
                    {(() => {
                        const totalQty = currentData.generic_total_qty;
                        const handleGenericPageChange = (page: number) => {
                            setGenericPage(page);
                            fetchPage({ genericPage: page });
                        };
                        return (
                            <>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className={styles.descCol}>
                                                <HelpHint text={genericItemColTooltip(title)}>Item</HelpHint>
                                            </th>
                                            <th className={styles.numCol}>
                                                <HelpHint text={COMBO_QUANTITY_COL}>Receipts</HelpHint>
                                            </th>
                                            <th className={styles.numCol}>
                                                <HelpHint text={genericPctColTooltip()}>%</HelpHint>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentData.generic.map((item, i) => {
                                            const isNoFixed = item.description === "No fixed interior";
                                            const isSelected = selectedGeneric === item.description;
                                            const isDimmed = selectedGeneric && !isSelected;
                                            const pct = totalQty > 0 ? (100 * item.count) / totalQty : 0;
                                            return (
                                                <tr
                                                    key={i}
                                                    className={`${styles.clickableRow} ${isSelected ? styles.selectedRow : ""} ${isDimmed ? styles.dimmedRow : ""} ${isNoFixed ? styles.noFixedRow : ""}`}
                                                    onClick={() => {
                                                        const newSelection = isSelected ? "" : item.description;
                                                        setSelectedGeneric(newSelection);
                                                        setWithQtyPage(1);
                                                        fetchPage({
                                                            genericFilter: newSelection || null,
                                                            withQtyPage: 1,
                                                        });
                                                        onGenericClick?.(newSelection);
                                                    }}
                                                >
                                                    <td className={styles.descCol}>
                                                        <ComboDescription
                                                            text={item.description}
                                                            itemNoLookup={currentData?.item_desc_item_nos}
                                                            replacementsLookup={currentData?.item_desc_replacements}
                                                        />
                                                    </td>
                                                    <td className={styles.numCol}>{item.count.toLocaleString()}</td>
                                                    <td className={styles.numCol}>{pct.toFixed(1)}%</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className={styles.totalRow}>
                                            <td className={styles.descCol}>
                                                <strong>Total</strong>
                                            </td>
                                            <td className={styles.numCol}>
                                                <strong>{totalQty.toLocaleString()}</strong>
                                            </td>
                                            <td className={styles.numCol}>
                                                <strong>100.0%</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <Pagination
                                    currentPage={genericPage}
                                    totalItems={currentData.generic_total}
                                    pageSize={genericPageSize}
                                    onPageChange={handleGenericPageChange}
                                    onPageSizeChange={(s) => {
                                        setGenericPageSize(s);
                                        setGenericPage(1);
                                        fetchPage({ genericPage: 1, genericSize: s });
                                    }}
                                    loading={isGenericStale}
                                />
                            </>
                        );
                    })()}
                </div>

                {/* Right column: with-count list */}
                <div className={`${styles.rightColumn} ${isWithQtyStale ? styles.stale : ""}`}>
                    <h4 className={styles.columnTitle}>
                        {title} Combinations
                        <HelpHint text={withQtyListTooltip(title)} className={styles.subtitle}>
                            (exact item count)
                        </HelpHint>
                    </h4>
                    {(() => {
                        const totalQty = currentData.with_qty_total_qty;
                        const handleWithQtyPageChange = (page: number) => {
                            setWithQtyPage(page);
                            fetchPage({ withQtyPage: page });
                        };
                        return (
                            <>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className={styles.descCol}>
                                                <HelpHint text={withQtyItemColTooltip(title)}>Item</HelpHint>
                                            </th>
                                            <th className={styles.numCol}>
                                                <HelpHint text={COMBO_QUANTITY_COL}>Receipts</HelpHint>
                                            </th>
                                            <th className={styles.numCol}>
                                                <HelpHint text={withQtyPctColTooltip()}>%</HelpHint>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentData.with_qty.map((item, i) => {
                                            const isNoFixed = item.description === "No fixed interior";
                                            const pct = totalQty > 0 ? (100 * item.count) / totalQty : 0;
                                            return (
                                                <tr key={i} className={isNoFixed ? styles.noFixedRow : ""}>
                                                    <td className={styles.descCol}>
                                                        <ComboDescription
                                                            text={item.description}
                                                            itemNoLookup={currentData?.item_desc_item_nos}
                                                            replacementsLookup={currentData?.item_desc_replacements}
                                                        />
                                                    </td>
                                                    <td className={styles.numCol}>{item.count.toLocaleString()}</td>
                                                    <td className={styles.numCol}>{pct.toFixed(1)}%</td>
                                                </tr>
                                            );
                                        })}
                                        <tr className={styles.totalRow}>
                                            <td className={styles.descCol}>
                                                <strong>Total</strong>
                                            </td>
                                            <td className={styles.numCol}>
                                                <strong>{totalQty.toLocaleString()}</strong>
                                            </td>
                                            <td className={styles.numCol}>
                                                <strong>100.0%</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <Pagination
                                    currentPage={withQtyPage}
                                    totalItems={currentData.with_qty_total}
                                    pageSize={withQtyPageSize}
                                    onPageChange={handleWithQtyPageChange}
                                    onPageSizeChange={(s) => {
                                        setWithQtyPageSize(s);
                                        setWithQtyPage(1);
                                        fetchPage({ withQtyPage: 1, withQtySize: s });
                                    }}
                                    loading={isWithQtyStale}
                                />
                            </>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}
